const statusColors = {
  green: "#4caf50",
  amber: "#f89746",
  orange: "#f89746",
  red: "#f44336",
  grey: "#9e9e9e",
  gray: "#9e9e9e",
  neutral: "#9e9e9e",
};

const breakdownLineColor = "#4D93D9";

const ensureArray = (maybeArray) => (Array.isArray(maybeArray) ? maybeArray : []);

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === "null") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return null;
  const asString = String(value).trim();
  return asString || null;
};

const parseDateLabel = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) return "";
  const dmyMatch = normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return `${year}-${month}-${day}`;
  }
  const dateInstance = new Date(normalized);
  return Number.isNaN(dateInstance.getTime())
    ? normalized
    : dateInstance.toISOString().split("T")[0];
};

const formatAxisDateLabel = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) return "";
  const dateInstance = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(dateInstance.getTime())) return normalized;
  const day = String(dateInstance.getDate()).padStart(2, "0");
  const month = dateInstance.toLocaleString("en-US", { month: "short" });
  return `${day} ${month}`;
};

export const mortalityStatusColor = (status) => {
  const key = normalizeString(status)?.toLowerCase() || "neutral";
  return statusColors[key] || statusColors.neutral;
};

export const buildMortalityChartData = (payload, options = {}) => {
  const { limit = 7, datasetType = "line", label = "Mortality", datasetOverrides = {} } = options;
  const dates = ensureArray(payload?.dates);
  const days = ensureArray(payload?.day);
  const mortality = ensureArray(payload?.mortality);
  const mortalitiesOnly = ensureArray(payload?.mortalities_only ?? payload?.mortality_only);
  const cullsOnly = ensureArray(payload?.culls_only);
  const deviationp = ensureArray(payload?.deviationp);
  const mortalityRates = ensureArray(payload?.mortality_rate);
  const statuses = ensureArray(payload?.status);
  const increments = ensureArray(payload?.increment);

  const totalPoints = Math.max(
    dates.length,
    days.length,
    mortality.length,
    mortalitiesOnly.length,
    cullsOnly.length,
    deviationp.length,
    mortalityRates.length,
    statuses.length,
    increments.length
  );

  const points = [];
  for (let index = 0; index < totalPoints; index += 1) {
    const labelValue = parseDateLabel(dates[index]);
    if (!labelValue) continue;
    const sortValue = Date.parse(labelValue);
    points.push({
      label: normalizeNumber(days[index]) ?? formatAxisDateLabel(labelValue),
      day: normalizeNumber(days[index]),
      date: labelValue,
      sortValue: Number.isNaN(sortValue) ? index : sortValue,
      mortality: normalizeNumber(mortality[index]),
      mortalities_only: normalizeNumber(mortalitiesOnly[index]),
      culls_only: normalizeNumber(cullsOnly[index]),
      deviationp: normalizeNumber(deviationp[index]),
      mortality_rate: normalizeNumber(mortalityRates[index]),
      status: statuses[index],
      increment: normalizeNumber(increments[index]),
    });
  }

  const sortedPoints = points.sort((a, b) => a.sortValue - b.sortValue);
  const trimmedPoints =
    limit && Number.isFinite(limit) && limit > 0 ? sortedPoints.slice(-limit) : sortedPoints;

  if (!trimmedPoints.length) {
    return { labels: [], datasets: [] };
  }

  const labels = trimmedPoints.map((item) => item.label);
  const data = trimmedPoints.map((item) => item.mortality);
  const mortalitiesOnlyData = trimmedPoints.map((item) => item.mortalities_only);
  const cullsOnlyData = trimmedPoints.map((item) => item.culls_only);
  const metadata = trimmedPoints.map((item) => ({
    tooltipType: "mortalityBreakdown",
    day: item.day,
    date: item.date,
    deviationp: item.deviationp,
    mortality_rate: item.mortality_rate,
    increment: item.increment,
    mortalities_only: item.mortalities_only,
    culls_only: item.culls_only,
  }));
  const backgroundColor = trimmedPoints.map((item) => mortalityStatusColor(item.status));

  const isBar = datasetType === "bar";
  const baseDataset = isBar
    ? {
        type: "bar",
        barThickness: 18,
        maxBarThickness: 18,
        borderRadius: 6,
      }
    : {
        type: "line",
        borderColor: "#F2F2F2",
        borderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 10,
        pointBorderWidth: 0,
        pointHoverBorderWidth: 0,
        fill: false,
        tension: 0.4,
        spanGaps: false,
      };

  const dataset = {
    label,
    data,
    backgroundColor,
    pointBackgroundColor: isBar ? undefined : backgroundColor,
    metadata,
    showLegend: false,
    ...baseDataset,
    ...datasetOverrides,
  };

  if (isBar) {
    return { labels, datasets: [dataset] };
  }

  const secondaryMetadata = trimmedPoints.map((item) => ({
    tooltipType: "mortalityBreakdownPart",
    day: item.day,
    date: item.date,
    total: item.mortality,
    mortalities_only: item.mortalities_only,
    culls_only: item.culls_only,
  }));

  const mortalitiesDataset = {
    label: "Mortality",
    data: mortalitiesOnlyData,
    backgroundColor: breakdownLineColor,
    borderColor: breakdownLineColor,
    pointBackgroundColor: breakdownLineColor,
    pointBorderColor: breakdownLineColor,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 1,
    fill: false,
    tension: 0.4,
    spanGaps: false,
    metadata: secondaryMetadata,
    showLegend: true,
    datalabels: { display: false },
  };

  const cullsDataset = {
    label: "Culls",
    data: cullsOnlyData,
    backgroundColor: breakdownLineColor,
    borderColor: breakdownLineColor,
    pointBackgroundColor: breakdownLineColor,
    pointBorderColor: breakdownLineColor,
    pointRadius: 2,
    pointHoverRadius: 5,
    borderWidth: 1,
    borderDash: [6, 4],
    fill: false,
    tension: 0.4,
    spanGaps: false,
    metadata: secondaryMetadata,
    showLegend: true,
    datalabels: { display: false },
  };

  return { labels, datasets: [dataset, mortalitiesDataset, cullsDataset] };
};
